import { useState } from 'react';
import { useAction, useApiQuery } from '../../engine/api/hooks';
import { Badge, EmptyState, Skeleton } from '../../engine/design/primitives/Misc';
import { Button, IconButton } from '../../engine/design/primitives/Button';
import { Icon } from '../../engine/design/icons';

// The copilot beside the ticket (docs/servicedesk-v2-proposal.md P4 tier 2). Four asks, one
// answer at a time, and every answer carries what a technician needs to judge it: how sure the
// model was, and Use it / No thanks — because a suggestion nobody can accept or refuse is just
// noise, and the accept/reject is what the accuracy numbers are made of.

interface Suggestion {
  id: string;
  kind: string;
  action_class: string;
  payload: Record<string, unknown>;
  confidence: string | null;
  state: string;
  created_at: string;
}

interface RunOutcome {
  run_id: string;
  status: 'succeeded' | 'failed' | 'skipped';
  value: unknown;
  confidence: number | null;
  reason?: string;
  suggestion?: Suggestion;
}

const ASKS: Array<{ kind: string; label: string; icon: 'sparkles' | 'send' | 'target' | 'bookmark' }> = [
  { kind: 'summary', label: 'Summarise', icon: 'sparkles' },
  { kind: 'reply_draft', label: 'Draft a reply', icon: 'send' },
  { kind: 'next_action', label: 'Next action', icon: 'target' },
  { kind: 'kb_draft', label: 'Write it up', icon: 'bookmark' },
];

/** Prose comes back as { text }; structured answers are shown as their own fields. */
function bodyOf(payload: Record<string, unknown>): string {
  if (typeof payload['text'] === 'string') return payload['text'];
  return Object.entries(payload)
    .filter(([k]) => k !== 'confidence')
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${Array.isArray(v) ? v.join(', ') : String(v ?? '—')}`)
    .join('\n');
}

export function AssistPanel({ ticketId, onApplied }: { ticketId: string; onApplied?: () => void }) {
  const open = useApiQuery<{ data: Suggestion[] }>(['ticket-suggestions', ticketId], `/v1/tickets/${ticketId}/suggestions`, { enabled: Boolean(ticketId) });
  const assist = useAction<RunOutcome>('tickets', 'assist', { toast: { success: false } });
  const accept = useAction('ai-suggestions', 'accept', { toast: { success: 'Noted — thanks' } });
  const reject = useAction('ai-suggestions', 'reject', { toast: { success: 'Noted' } });
  const rate = useAction('ai-suggestions', 'feedback', { toast: { success: 'Thanks' } });
  const [notice, setNotice] = useState<string | null>(null);

  const ask = (kind: string) => {
    setNotice(null);
    assist.mutate(
      { id: ticketId, body: { kind } },
      {
        onSuccess: (r) => {
          const res = r as RunOutcome;
          // A refusal is shown as the plain sentence it is, never as an empty panel.
          if (res.status !== 'succeeded') setNotice(res.reason ?? 'The copilot had nothing to add.');
          void open.refetch();
        },
      },
    );
  };

  const decide = (id: string, take: boolean) => {
    const m = take ? accept : reject;
    m.mutate({ id, body: {} }, { onSuccess: () => { void open.refetch(); if (take) onApplied?.(); } });
  };

  const suggestions = open.data?.data ?? [];

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
      <div className="hf-chiprow">
        {ASKS.map((a) => (
          <Button key={a.kind} size="sm" variant="outline" icon={a.icon} busy={assist.isPending} onClick={() => ask(a.kind)}>
            {a.label}
          </Button>
        ))}
      </div>

      {notice && (
        <p className="hf-muted" role="status" style={{ fontSize: 'var(--t-12)', margin: 0 }}>
          <Icon name="info" size={12} /> {notice}
        </p>
      )}

      {open.isLoading ? (
        <Skeleton lines={4} />
      ) : suggestions.length === 0 ? (
        <EmptyState title="Nothing suggested yet" hint="Ask for a summary, a draft, or the next action." icon="sparkles" />
      ) : (
        <ul className="hf-stack" style={{ gap: 'var(--s-2)', listStyle: 'none', padding: 0, margin: 0 }}>
          {suggestions.map((s) => (
            <li key={s.id} className="hf-card pad hf-stack" style={{ gap: 'var(--s-2)' }}>
              <div className="hf-row" style={{ fontSize: 'var(--t-11)' }}>
                <Badge tone="accent" dot={false}>{s.kind.replace(/_/g, ' ')}</Badge>
                {s.confidence !== null && (
                  <Badge tone={Number(s.confidence) >= 0.8 ? 'pos' : Number(s.confidence) >= 0.5 ? 'warn' : 'muted'} dot={false}>
                    {Math.round(Number(s.confidence) * 100)}% sure
                  </Badge>
                )}
                <span className="hf-grow" />
                <IconButton icon="check" label="This was useful" size="sm" onClick={() => rate.mutate({ id: s.id, body: { rating: 1 } })} />
                <IconButton icon="x" label="This was not useful" size="sm" onClick={() => rate.mutate({ id: s.id, body: { rating: -1 } })} />
              </div>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 'var(--t-13)' }}>{bodyOf(s.payload)}</p>
              <div className="hf-row">
                <Button size="sm" variant="primary" icon="check" busy={accept.isPending} onClick={() => decide(s.id, true)}>Use it</Button>
                <Button size="sm" variant="ghost" icon="x" busy={reject.isPending} onClick={() => decide(s.id, false)}>No thanks</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
